import React, { FC, useState } from 'react';

import { Popover, PopoverContent, PopoverTrigger } from '@column/ui';

import { useBankAccountOptions } from '~/components/fields/BankAccountDropdown';
import { POPOVER_CONTENT_CLASS, SelectorSearch } from '~/lib/components/selectors';

import { TableFilterTrigger } from './TableFilterTrigger';

const ALL_ACCOUNTS = '__all__';

interface TableBankAccountFilterProps {
  value?: string;
  onChange: (value: string | undefined) => void;
}

export const TableBankAccountFilter: FC<TableBankAccountFilterProps> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const { bankAccounts, activeBankAccount, search, handleScrollEnd, isLoading } = useBankAccountOptions({ value });
  const selected = bankAccounts.find((account) => account.id === value) ?? activeBankAccount;

  const options = [
    ...(value ? [{ id: ALL_ACCOUNTS, primary: 'All accounts' }] : []),
    ...bankAccounts.map((account) => ({
      id: account.id,
      primary: account.display_name || account.description || 'Unnamed',
      secondary: account.default_account_number ? (
        <span className="font-mono">
          <span className="text-muted-foreground/60">ACCT </span>
          {account.default_account_number}
        </span>
      ) : undefined,
    })),
  ];

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) search('');
      }}
    >
      <PopoverTrigger asChild>
        <TableFilterTrigger isActive={Boolean(value)} isOpen={open}>
          {value ? selected?.display_name || selected?.description || 'Account' : 'Account'}
        </TableFilterTrigger>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={8} className={POPOVER_CONTENT_CLASS}>
        <SelectorSearch
          options={options}
          isLoading={isLoading}
          searchPlaceholder="Search accounts…"
          emptyMessage="No accounts found"
          onSearch={search}
          onSelect={(id) => {
            onChange(id === ALL_ACCOUNTS ? undefined : id);
            setOpen(false);
            search('');
          }}
          onScrollEnd={handleScrollEnd}
        />
      </PopoverContent>
    </Popover>
  );
};
